import { useEffect, useState } from 'react';
import { useAdvancedGameLogic } from '../../hooks/useAdvancedGameLogic';
import { useGameTimer } from '../../hooks/useGameTimer';
import { GameControls } from '../Game/GameControls';
import { GameStats } from '../Game/GameStats';
import { GameTimer } from '../Game/GameTimer';
import { AdvancedGameDisplay } from './AdvancedGameDisplay';
import { DifficultySelector } from './DifficultySelector';
import { GameStartPage } from '../GameStartPage';

export const AdvancedGame: React.FC = () => {
  const [hasStarted, setHasStarted] = useState(false);
  const {
    level,
    setDifficultyLevel,
    currentQuestion,
    showAnswer,
    skipQuestion,
    previousQuestion,
    nextQuestion,
    stats,
    isBlocked,
  } = useAdvancedGameLogic();

  const {
    time,
    isRunning,
    questionsInCurrentSession,
    remainingTime,
    toggleTimer,
    resetTimer,
    setTimerDuration,
    incrementQuestions,
    getQuestionsPerMinute,
  } = useGameTimer();

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName?.toLowerCase();
        const isInteractive = ['input', 'textarea', 'button', 'select', 'a'].includes(tag) ||
          !!target.getAttribute?.('role')?.match(/button|link/);
        if (isInteractive) return;
      }

      if (e.code === 'ArrowLeft') {
        previousQuestion();
      } else if (e.code === 'ArrowRight' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        handleNextQuestion();
      } else if (e.code === 'KeyS') {
        skipQuestion();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [previousQuestion, nextQuestion, skipQuestion, incrementQuestions, showAnswer]);

  const handleNextQuestion = () => {
    if (!showAnswer) {
      incrementQuestions();
    }
    nextQuestion();
  };

  if (!hasStarted) {
    return <GameStartPage gameTitle="Advanced Math" onStart={() => setHasStarted(true)} />;
  }

  return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-gray-700 transition-colors duration-200">
          <GameTimer
            time={time}
            isRunning={isRunning}
            remainingTime={remainingTime}
            onToggle={toggleTimer}
            onReset={resetTimer}
            onSetDuration={setTimerDuration}
          />

          <DifficultySelector
            currentLevel={level}
            onSelectLevel={setDifficultyLevel}
          />

          <AdvancedGameDisplay
            question={currentQuestion.question}
            answer={currentQuestion.answer}
            explanation={currentQuestion.explanation}
            showAnswer={showAnswer}
            isBlocked={isBlocked}
            level={level}
          />

          <GameControls
            onPrevious={previousQuestion}
            onSkip={skipQuestion}
            onNext={handleNextQuestion}
          />

          <GameStats
            stats={stats}
            questionsInCurrentSession={questionsInCurrentSession}
            questionsPerMinute={Number(getQuestionsPerMinute())}
            isTimerRunning={isRunning}
          />
        </div>
      </div>
    );
};